import { useEffect, useState } from 'react';
import {
    TableContainer,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TextField,
    IconButton,
    Typography,
} from '@mui/material';
import { Reviews, ReviewsOutlined } from '@mui/icons-material';
import { OfferSubmissionItem } from '../dto/offer-submission.dto';
import { useTranslation } from 'react-i18next';
import { formatToGermanCurrency } from 'src/common/toGermanNumber';

function DelayedTotalPriceTextField({ value, onChange }: { value: string; onChange: (val: string) => void }) {
    const [localValue, setLocalValue] = useState(value);

    useEffect(() => {
        setLocalValue(value);
    }, [value]);

    return (
        <TextField
            variant="standard"
            size="small"
            type="number"
            sx={{ input: { textAlign: 'right' } }}
            value={localValue}
            onChange={(e) => setLocalValue(e.target.value)}
            onBlur={() => {
                if (localValue !== value) {
                    onChange(localValue);
                }
            }}
        />
    );
}

interface BoQTableViewProps {
    totalPrice: string;
    items: OfferSubmissionItem[];
    editMode: EditMode;
    onMarkError: (ordinal: string) => void;
    onItemChange?: (item: OfferSubmissionItem) => void;
    onTotalPriceChange?: (totalPrice: string) => void;
}

export enum EditMode {
    READ_ONLY,
    EDITABLE,
    EDITABLE_UPDATE_TOTAL_PRICES,
}

export default function BoQTableView({
    totalPrice,
    items,
    editMode,
    onMarkError,
    onItemChange,
    onTotalPriceChange,
}: BoQTableViewProps) {
    const { t } = useTranslation();
    const isEditable = editMode === EditMode.EDITABLE || editMode === EditMode.EDITABLE_UPDATE_TOTAL_PRICES;

    // Automatic calculation of total prices for line items
    // This is only done in EDITABLE_UPDATE_TOTAL_PRICES mode and
    // only if the total price hasn't been touched yet (= is 0)
    useEffect(() => {
        if (editMode !== EditMode.EDITABLE_UPDATE_TOTAL_PRICES || !onItemChange) return;

        items.forEach((item) => {
            if (item.type === 'LINE_ITEM' && Number(item.totalPrice) === 0) {
                const qty = Number(item.quantity);
                const price = Number(item.unitPrice);
                const calculatedTotal = (isNaN(qty) ? 0 : qty) * (isNaN(price) ? 0 : price);
                const calculatedTotalString = calculatedTotal.toString();

                if (calculatedTotalString !== item.totalPrice) {
                    onItemChange({ ...item, totalPrice: calculatedTotalString });
                }
            }
        });
    }, [items, editMode, onItemChange]);

    return (
        <TableContainer component={Paper}>
            <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.4, px: 1 } }}>
                <TableHead>
                    <TableRow
                        sx={{
                            backgroundColor: 'primary.main',
                            '& .MuiTableCell-root': { color: 'primary.contrastText' },
                        }}
                    >
                        <TableCell>{t('Ordinal')}</TableCell>
                        <TableCell align="right">{t('Quantity')}</TableCell>
                        <TableCell>{t('Unit')}</TableCell>
                        <TableCell>{t('Short Text')}</TableCell>
                        <TableCell align="right">{t('Unit Price (€)')}</TableCell>
                        <TableCell align="right">{t('Total Price (€)')}</TableCell>
                        <TableCell></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    <TableRow
                        sx={{
                            backgroundColor: 'secondary.main',
                            '& .MuiTableCell-root': {
                                fontWeight: 'bold',
                                color: 'secondary.contrastText',
                            },
                        }}
                    >
                        <TableCell colSpan={5}>∑ {t('Total Sum')}</TableCell>
                        <TableCell align="right">
                            {isEditable ? (
                                <DelayedTotalPriceTextField
                                    value={totalPrice}
                                    onChange={(val) => onTotalPriceChange?.(val)}
                                />
                            ) : (
                                <Typography>{formatToGermanCurrency(totalPrice)}</Typography>
                            )}
                        </TableCell>
                        <TableCell></TableCell>
                    </TableRow>
                    {items.map((item) => (
                        <TableRow
                            key={item.ordinal}
                            sx={{
                                backgroundColor: item.type === 'HEADER' ? 'grey.300' : 'transparent',
                                '& .MuiTableCell-root': {
                                    fontWeight: item.type === 'HEADER' ? 'bold' : 'normal',
                                },
                            }}
                        >
                            <TableCell>{item.ordinal}</TableCell>
                            <TableCell align="right">{item.type === 'LINE_ITEM' ? item.quantity : ''}</TableCell>
                            <TableCell>{item.type === 'LINE_ITEM' ? item.quantityUnit : ''}</TableCell>
                            <TableCell>
                                {item.shortText}
                                {item.type === 'LINE_ITEM' && item.complementText && (
                                    <>
                                        <br />
                                        <Typography variant="caption" color="textSecondary">
                                            {item.complementText}
                                        </Typography>
                                    </>
                                )}
                            </TableCell>
                            <TableCell align="right">
                                {item.type === 'LINE_ITEM' &&
                                    (isEditable ? (
                                        <DelayedTotalPriceTextField
                                            value={item.unitPrice}
                                            onChange={(val) => onItemChange?.({ ...item, unitPrice: val })}
                                        />
                                    ) : (
                                        <Typography>{formatToGermanCurrency(item.unitPrice)}</Typography>
                                    ))}
                            </TableCell>
                            <TableCell align="right">
                                {isEditable ? (
                                    <DelayedTotalPriceTextField
                                        value={item.totalPrice}
                                        onChange={(val) => onItemChange?.({ ...item, totalPrice: val })}
                                    />
                                ) : (
                                    <Typography>{formatToGermanCurrency(item.totalPrice)}</Typography>
                                )}
                            </TableCell>
                            <TableCell>
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => onMarkError(item.ordinal)}
                                    sx={{ p: 0 }}
                                >
                                    {item.assessmentFailReasons['TECHNISCHE_PRUEFUNG_T1'] ? (
                                        <Reviews />
                                    ) : (
                                        <ReviewsOutlined />
                                    )}
                                </IconButton>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
